import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Text, Title, TrevorIcon } from '@trevor/ui-components';
import { SavedQuery, SavedQueryMeta } from '@/views/SavedQueries/types';
import { usePrefetchDependantSavedQueries } from '@/features/savedQueriesWidget/hooks';
import { DEPENDANT_QUERIES_CLICKED } from '@/actions/constants';
import { setDependantQueriesIds } from '@/features/dependantQueries/dependantQueriesSlice';
import { formatTimestamp } from '@/util/helpers/time-helpers';
import { getLastRefreshedInfo, getStepsInfo } from './helpers';

const DependantQueries = styled(Text)`
  & > span {
    border-bottom: 1px solid #666;
  }
`;

export type InfoProps = {
  savedQuery: SavedQuery;
  meta: Maybe<SavedQueryMeta>;
};

export const Info: React.FC<InfoProps> = ({ savedQuery, meta }) => {
  const dispatch = useDispatch();

  const dependantQueries = [
    ...new Set([
      ...savedQuery.dependantQuerySeriesIds,
      ...savedQuery.dependantJoinSeriesIds,
    ]),
  ];

  const numberOfDependantQueries = dependantQueries.length;

  const handleDependantQueriesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(setDependantQueriesIds(dependantQueries));
    dispatch({
      type: DEPENDANT_QUERIES_CLICKED,
    });
  };

  const prefetchDependantSavedQueries =
    usePrefetchDependantSavedQueries(dependantQueries);

  return (
    <>
      <Title>{savedQuery.name()}</Title>
      {meta && (
        <>
          {numberOfDependantQueries > 0 && (
            <DependantQueries
              icon={<TrevorIcon name="branch" />}
              onClick={handleDependantQueriesClick}
              onMouseEnter={prefetchDependantSavedQueries}
            >
              {numberOfDependantQueries} dependent{' '}
              {numberOfDependantQueries === 1 ? 'query' : 'queries'}
            </DependantQueries>
          )}
          <Text icon={<TrevorIcon name="rocket" size="sm" />}>
            {getStepsInfo(meta, formatTimestamp(savedQuery.saved()) as string)}
          </Text>
          {meta.lastRefreshed() && (
            <Text icon={<TrevorIcon name="refresh" size="sm" />}>
              {getLastRefreshedInfo(meta)}
            </Text>
          )}
        </>
      )}
    </>
  );
};
